import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, googleAuthProvider } from '../../firebase';
// 🔽 Added: sendPasswordResetEmail
import { signInWithEmailAndPassword, signInWithPopup, sendPasswordResetEmail } from 'firebase/auth';
import { db } from '../../firebase'; 
import { doc, getDoc } from 'firebase/firestore'; 
import '../../styles/AuthStyles.css';

// Utility icons
const BackArrowIcon = () => (
    <svg className="back-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"></path>
    </svg>
);
const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="google-icon" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.24 10.29v3.08h6.14c-.25 1.57-1.14 2.82-2.58 3.65l3.22 2.5a10.02 10.02 0 0 0 3.78-8.91c0-4.9-3.99-8.9-8.9-8.9s-8.9 3.99-8.9 8.9a8.88 8.88 0 0 0 2.58 6.27l3.22-2.5a5.27 5.27 0 0 1-2.02-3.77z" fill="#4285F4"/>
        <path d="M5.44 14.86a5.3 5.3 0 0 1 0-5.72l-3.22-2.5a8.91 8.91 0 0 0 0 10.72l3.22-2.5z" fill="#34A853"/>
        <path d="M12.24 5.33c1.62 0 3.09.56 4.24 1.51l2.87-2.87c-1.89-1.78-4.32-2.77-7.11-2.77a8.9 8.9 0 0 0-6.31 2.61l3.22 2.5c1.15-.95 2.62-1.51 4.24-1.51z" fill="#FBBC05"/>
        <path d="M12.24 21.67c-3.1 0-5.91-1.3-7.91-3.3l-3.22 2.5a10.9 10.9 0 0 0 11.13 0l-3.22-2.5c-1.39 1-3.08 1.57-4.8 1.57z" fill="#EA4335"/>
    </svg>
);

const EyeIconOpen = ({ onClick, className }) => (
    <svg onClick={onClick} className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);

const EyeIconClosed = ({ onClick, className }) => (
    <svg onClick={onClick} className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8s2.92-5.74 8-6m0 4a4 4 0 1 0 0-8M22 2L2 22"></path>
    </svg>
);


const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState(''); // 🔽 Added: For success confirmation
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false); 
    const navigate = useNavigate();

    // Toggle function
    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    // 🔽 Added: Handle Password Reset
    const handleForgotPassword = async () => {
        if (!email) {
            setError("Please enter your email address first.");
            return;
        }
        setError('');
        setMessage('');
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage("Reset link sent! Please check your email inbox.");
        } catch (err) {
            console.error('Login.js: Reset failed:', err.message);
            setError("Error: " + err.message);
        }
    };

    // Helper to fetch user role after successful authentication
    const fetchUserRoleAndNavigate = async (user) => {
        try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
                const data = userDocSnap.data();
                const fetchedRole = data.role;
                if (fetchedRole) {
                    localStorage.setItem('role', fetchedRole);
                    if (fetchedRole === 'client') {
                        navigate('/client/dashboard', { replace: true });
                    } else if (fetchedRole === 'customer') {
                        navigate('/customer/dashboard', { replace: true });
                    } else {
                        navigate('/login', { replace: true });
                    }
                } else {
                    navigate('/login', { replace: true });
                }
            } else {
                navigate('/login', { replace: true });
            }
        } catch (err) {
            console.error('Login.js: Error fetching role after login:', err);
            navigate('/login', { replace: true });
        }
    };


    const handleLogin = async (e) => {
        e.preventDefault();
        if (loading) return;

        setError('');
        setMessage(''); // Clear success message on login attempt
        setLoading(true);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await fetchUserRoleAndNavigate(userCredential.user);

        } catch (error) {
            console.error('Login.js: Login failed:', error.message);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        if (loading) return;

        setError('');
        setMessage('');
        setLoading(true);

        try {
            const userCredential = await signInWithPopup(auth, googleAuthProvider);
            await fetchUserRoleAndNavigate(userCredential.user);

        } catch (error) {
            console.error('Login.js: Google sign-in failed:', error.message);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-screen-container">
            <div className="auth-header-art">
                <button className="back-button" onClick={() => navigate(-1)} aria-label="Go Back">
                    <BackArrowIcon />
                </button>
                <div className="clean-logo-placeholder">
                    <span className="logo-text-large">
                        NUTRITION TRENDS
                    </span>
                    <p className="logo-subtext">Start Your Health Journey Today</p>
                </div>
            </div>

            <div className="auth-form-card">
                <h1 className="auth-title">Welcome Back!</h1>

                {error && <p className="auth-error">{error}</p>}
                {/* 🔽 Show success message */}
                {message && <p className="auth-success" style={{color: '#28a745', textAlign: 'center', marginBottom: '10px'}}>{message}</p>}
                
                <form onSubmit={handleLogin} className="auth-form">
                    <div className="input-group">
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email address"
                            required
                            disabled={loading}
                        />
                    </div>
                    <div className="input-group password-group"> 
                        <input
                            type={showPassword ? 'text' : 'password'} 
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            required
                            disabled={loading}
                        />
                        {showPassword ? (
                            <EyeIconOpen onClick={togglePasswordVisibility} className="password-toggle-icon" />
                        ) : (
                            <EyeIconClosed onClick={togglePasswordVisibility} className="password-toggle-icon" />
                        )}
                    </div>

                    {/* 🔽 Added: Forgot Password Link */}
                    <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '15px' }}>
                        <span 
                            onClick={handleForgotPassword} 
                            style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#4CAF50', fontWeight: '500' }}
                        >
                            Forgot Password?
                        </span>
                    </div>
                    
                    <button type="submit" className="primary-auth-button" disabled={loading}>
                        {loading ? 'LOGGING IN...' : 'LOG IN'}
                    </button>

                    <div className="or-divider">OR</div>

                    <button 
                        type="button" 
                        onClick={signInWithGoogle} 
                        disabled={loading}
                        className="google-auth-button"
                    >
                        <GoogleIcon />
                        {loading ? 'Signing In...' : 'Sign In with Google'}
                    </button>


                    <p className="auth-footer-text">
                        Don't have an account? <Link to="/signup" className="auth-link">Sign Up Now</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Login;